import { Parser, Value } from 'expr-eval-fork'

/**
 * Standard error codes for formula evaluation failures.
 */
export enum CalcErrorCode {
  INVALID_SYNTAX = 'INVALID_SYNTAX',
  MISSING_VARIABLE = 'MISSING_VARIABLE',
  DIVISION_BY_ZERO = 'DIVISION_BY_ZERO',
  EMPTY_EXPRESSION = 'EMPTY_EXPRESSION',
  UNKNOWN = 'UNKNOWN',
}

export interface CalcResult {
  value?: number
  errorCode?: CalcErrorCode
}

/**
 * Constant prefix used to identify variables within formula strings.
 */
const PREFIX = '$'

/**
 * Evaluates a mathematical expression string using a provided scope of values.
 */
export function calculate(expression: string, values: Record<string, unknown>): CalcResult {
  try {
    if (!expression || expression.trim() === '') {
      throw new Error('EMPTY_EXPRESSION')
    }

    // Normalize values: ensure all keys start with the prefix
    const normalizedValues: Record<string, unknown> = {}
    for (const key in values) {
      normalizedValues[ensurePrefix(key)] = values[key]
    }

    const parser = new Parser()
    const result = parser.evaluate(expression, normalizedValues as Value)

    // Check for Infinity or NaN (e.g., manual division by zero in expression)
    if (!Number.isFinite(result)) {
      throw new Error('DIVISION_BY_ZERO')
    }

    return { value: Number(result) }
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.message.includes('unexpected') || error.message.includes('Expected EOF')) {
        return { errorCode: CalcErrorCode.INVALID_SYNTAX }
      } else if (error.message.includes('undefined variable')) {
        return { errorCode: CalcErrorCode.MISSING_VARIABLE }
      } else if (error.message === 'DIVISION_BY_ZERO') {
        return { errorCode: CalcErrorCode.DIVISION_BY_ZERO }
      } else if (error.message === 'EMPTY_EXPRESSION') {
        return { errorCode: CalcErrorCode.EMPTY_EXPRESSION }
      }
    }

    return { errorCode: CalcErrorCode.UNKNOWN }
  }
}

/**
 * Helper to ensure a string starts with the designated variable prefix.
 */
const ensurePrefix = (str: string) => (str.startsWith(PREFIX) ? str : `${PREFIX}${str}`)

/**
 * Extracts variable names from an expression string, returning them without the prefix.
 * Example: "$a + $b" -> ["a", "b"]
 */
export function getVariables(expression: string): string[] {
  const matches = (expression || '').match(/\$\w+/g) || []
  return matches.map(x => x.substring(1))
}

/**
 * Replaces variable placeholders in an expression string.
 * Supports replacements with or without the prefix and ensures the output remains prefixed.
 */
export function replaceVariables(expression: string, replacements: Record<string, string>) {
  // Normalize the replacements object so all keys and values have the prefix
  const normalizedReplacements: Record<string, string> = {}
  for (const key in replacements) {
    normalizedReplacements[ensurePrefix(key)] = ensurePrefix(replacements[key]!)
  }

  return (expression || '').replace(/\$\w+/g, match => {
    return normalizedReplacements[match] || match
  })
}
